/**
 * Payermax dropin 编排：负责 SDK 实例确认、支付表单挂载及支付结果回调。
 */
import { createApp } from 'vue'
import { createPaymentDropinOrder, createPaymentDropinOrderDetail, getPaymentChannelSession } from '../apis/payment'
import { PaymentOrderInfo, PublishPaymentMethodItem } from '../apis/types/payment'
import { PAYMENT_APP_VERSION } from '../const'
import Payermax from '../views/payermax/Payermax.vue'
import { setupI18n } from '../i18n'
import { PaymentMethodType } from '../types/payment-method'
import { PaymentDependencyManager } from '../utils/dependency'
import { pollPaymentStatus } from '../utils/paymentOrder'

declare global {
  interface Window {
    PMdropin: any
  }
}

// 确认 SDK 已加载，兼容入口预加载和按需创建两种使用方式。
const getSdkInstance = async () => {
  if (window.PMdropin) return window.PMdropin

  await new PaymentDependencyManager().loadPayermax()
  if (!window.PMdropin) throw new Error('Payermax SDK is not available')
  return window.PMdropin
}

// 自定义主题
const customTheme = [
  {
    // 主题名称【必填】
    name: 'custom',
    base: 'light',
    style: `:root {
          --bg-primary: transparent;
          --padding-frame: 0px;
          --bg-color-input: #ffffff;
        }`,
  },
]

const getLocale = (locale: string) => {
  if (locale === 'zhCN') return 'zh'
  return locale
}

export const createPayermaxDropin = (config: {
  locale: string
  orderInfo: PaymentOrderInfo
  paymentMethod: PublishPaymentMethodItem
  sdkVersion?: string
  isSandbox?: boolean
  callbackUrl?: string
  onLoaded?: () => void
  onFailed?: (error?: any) => void
  onError?: (error: any) => void
  onLoading?: (loading: boolean) => void
  onCreateOrder?: (result: any) => void
  onCompleted?: () => void
  on3DS2Challenge?: () => void
  getPaymentId?: () => string
}) => {
  const {
    locale,
    orderInfo,
    paymentMethod,
    sdkVersion = PAYMENT_APP_VERSION,
    isSandbox = true,
    onFailed,
    callbackUrl,
  } = config
  const { pay_type: businessType } = orderInfo

  /**
   * 支付渠道返回完成后，确认支付单终态；仅成功终态允许通知调用方完成。
   */
  const completePayment = async (paymentId?: string) => {
    if (!paymentId) {
      config.onFailed?.(new Error('Payment ID is required to verify payment status'))
      return
    }

    try {
      const result = await pollPaymentStatus({ payment_id: paymentId })
      if (result.success) {
        config.onCompleted?.()
        return
      }

      const message = result.timeout ? 'Payment status verification timed out' : 'Payment status verification failed'
      config.onFailed?.(new Error(message))
    } catch (error) {
      config.onFailed?.(error)
    }
  }

  const getChannelSession = async () => {
    const res = await getPaymentChannelSession({
      payment_channel_type: 4,
      sys_order_id: orderInfo.sys_order_id,
      country_code: orderInfo.country_code,
      payment_method_id: paymentMethod.payment_method_id,
    })
    return res
  }

  const setupFrom = async (containerId: string) => {
    const sdk = await getSdkInstance()
    const { payment_method_type: type } = paymentMethod
    if (type === PaymentMethodType.APPLE_PAY && !sdk.isSupportApplePay()) {
      config.onError?.(new Error('Apple Pay is not supported'))
      return
    }

    const { client_key, session_key } = await getChannelSession()

    /** 公共选项 */
    const commonOptions: any = {
      clientKey: client_key,
      sessionKey: session_key,
      language: getLocale(locale),
      sandbox: isSandbox,
    }

    let dropinInstance: any = null

    switch (type) {
      case PaymentMethodType.CARD:
        dropinInstance = sdk.create('card', {
          ...commonOptions,
          theme: 'custom',
          // 由于一次性支付保存卡信息的token无法用于订阅，因此这里隐藏保存卡信息
          // hideSaveCard: true,
          hideSaveCard: false,
          // 忽略推荐卡
          hideRecommendAccount: true,
          customTheme,
        })

        const app = createApp(Payermax, {
          orderInfo,
          dropinInstance,
          onLoaded: config.onLoaded,
          onError: config.onError,
          onPayButtonClick,
        })

        setupI18n(app)

        app.mount(containerId)

        break
      case PaymentMethodType.GOOGLE_PAY:
        dropinInstance = sdk.create('googlepay', {
          ...commonOptions,
          payButtonConfig: {
            width: '100%',
            height: '48px',
          },
        })

        dropinInstance.mount(containerId)

        dropinInstance.on('load', (load) => {
          console.log('load :>> ', load)
          if (load.code === 'SUCCESS') {
            config.onLoaded?.()
          } else {
            config.onError?.(new Error(load.msg))
          }
        })

        // payButtonClick
        dropinInstance.on('payButtonClick', onPayButtonClick)
        break
      case PaymentMethodType.APPLE_PAY:
        dropinInstance = sdk.create('applepay', {
          ...commonOptions,
          setpayButtonStyle: `width: 100%; height: 48px;`,
        })

        dropinInstance.mount(containerId)

        dropinInstance.on('ready', (ready) => {
          console.log('ready :>> ', ready)
          config.onLoaded?.()
        })

        dropinInstance.on('payButtonClick', onPayButtonClick)
        break
    }

    async function onPayButtonClick() {
      config.onLoading?.(true)
      dropinInstance.emit('setDisabled', true) // 点击支付按钮后冻结表单，防止重复提交支付过程

      try {
        const state = await dropinInstance.emit('canMakePayment', {
          mitManagementUrl: callbackUrl || '',
        })
        if (state.code !== 'APPLY_SUCCESS') {
          throw new Error(state.msg)
        }

        console.log('state :>> ', state)

        // 是否保存卡信息
        const storePaymentMethod = businessType === 2 || state.data?.agreementAccepted || false

        /** 创建支付订单参数 */
        const data = {
          ...state.data,
          sessionKey: session_key,
          userAgent: navigator.userAgent || '',
        }

        const result = await createPaymentDropinOrder({
          dropin_channel_data: JSON.stringify(data),
          payment_channel_type: 4,
          payment_method_id: paymentMethod!.payment_method_id,
          sys_order_id: orderInfo?.sys_order_id,
          sdk_version: sdkVersion,
          os: 1,
          save_card_info: storePaymentMethod ? 1 : 2,
        })

        config.onCreateOrder?.(result)

        const { result_data, payment_id } = result

        if (result_data.code !== 'APPLY_SUCCESS') {
          dropinInstance.emit('payFail')
          throw new Error(result_data.msg)
        }

        // ApplePay完成关闭组件
        if (type === PaymentMethodType.APPLE_PAY) {
          dropinInstance.emit('paySuccess')
        }

        if (result_data.data?.status === 'SUCCESS') {
          return await completePayment(payment_id)
        }

        // 处理3ds挑战
        await handle3DSChallenge(dropinInstance, result_data.data?.redirectUrl, payment_id)
      } catch (error) {
        config.onFailed?.(error)
        dropinInstance.emit('setDisabled', false) // 发生异常后解冻表单
      } finally {
        dropinInstance.emit('setDisabled', false) // 发生异常后解冻表单
        config.onLoading?.(false)
      }
    }
  }

  // 3ds action
  const setupFromAction = async (data: any) => {
    const sdk = await getSdkInstance()
    const { client_key, session_key } = await getChannelSession()

    /** 公共选项 */
    const commonOptions: any = {
      clientKey: client_key,
      sessionKey: session_key,
      language: getLocale(locale),
      sandbox: isSandbox,
    }

    const dropinInstance = sdk.create('card', {
      ...commonOptions,
      theme: 'custom',
      // 由于一次性支付保存卡信息的token无法用于订阅，因此这里隐藏保存卡信息
      hideSaveCard: true,
      customTheme,
    })

    // 处理3DS2挑战
    await handle3DSChallenge(dropinInstance, data.redirectUrl, config.getPaymentId?.())
  }

  // 处理3DS2挑战
  async function handle3DSChallenge(dropinInstance: any, threeDSUrl: string, paymentId?: string) {
    // 触发3DS2挑战
    config.on3DS2Challenge?.()

    const detailState = await dropinInstance
      .create3DSPopup({
        url: threeDSUrl,
      })
      .catch((error) => {
        console.log('error :>> ', error)
        if (error.code === 'USER_CANCEL') {
          // 商⼾也可不做处理，但是应允许⽤⼾继续点击⽀付按钮
          return error
        } else {
          throw error
        }
      })
    console.log('detailState :>> ', detailState)

    if (detailState.code === '3DS_PROCESSED') {
      // 3DS 流程完成，继续⽀付流程
      console.log('3DS 流程完成', detailState.data)
      // 此处商⼾需要维持现有逻辑，商⼾侧应该主动查询⽀付结果 继续商⼾侧后续的交互

      await completePayment(paymentId)
    } else {
      config.onFailed?.(new Error(detailState.msg))
    }
  }

  return {
    setupFrom,
    setupFromAction,
  }
}

// 判断是否需要3DS2挑战
export const isNeedPayermax3DS2Challenge = (result_data: any) => {
  return !!result_data?.data?.redirectUrl
}
